class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    this.stripDefaultPriceRangeValues(form, formData);
    return new URLSearchParams(formData).toString();
  }

  stripDefaultPriceRangeValues(form, formData) {
    if (!form || !formData) return;

    form.querySelectorAll('price-range').forEach((priceRange) => {
      const inputs = priceRange.querySelectorAll('input.field__input');
      const minInput = inputs[0];
      const maxInput = inputs[1];

      if (minInput?.name && this.isDefaultPriceValue(minInput.value, minInput.getAttribute('data-min') || '0')) {
        formData.delete(minInput.name);
      }

      if (maxInput?.name && this.isDefaultPriceValue(maxInput.value, maxInput.getAttribute('data-max'))) {
        formData.delete(maxInput.name);
      }
    });
  }

  isDefaultPriceValue(value, defaultValue) {
    const parsedValue = this.parseFacetPriceValue(value);
    const parsedDefault = this.parseFacetPriceValue(defaultValue);

    if (!Number.isFinite(parsedValue)) return true;
    if (!Number.isFinite(parsedDefault)) return false;

    return Math.abs(parsedValue - parsedDefault) < 0.01;
  }

  parseFacetPriceValue(value) {
    if (value === null || value === undefined) return NaN;

    const raw = String(value)
      .trim()
      .replace(/[^\d.,\s'-]/g, '')
      .replace(/\s|'/g, '');

    if (!raw) return NaN;

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const decimalSeparator = lastComma > lastDot ? ',' : '.';

    let normalized = raw;

    if (lastComma !== -1 && lastDot !== -1) {
      if (decimalSeparator === ',') {
        normalized = raw.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = raw.replace(/,/g, '');
      }
    } else if (lastComma !== -1) {
      normalized = raw.replace(',', '.');
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.textInputs = Array.from(this.querySelectorAll('input.field__input'));
    this.minInput = this.textInputs[0];
    this.maxInput = this.textInputs[1];

    this.slider = this.querySelector('[data-c2b-price-slider]');
    this.minSlider = this.querySelector('.c2b-price-slider__input--min');
    this.maxSlider = this.querySelector('.c2b-price-slider__input--max');
    this.rangeFill = this.querySelector('.c2b-price-slider__range-fill');

    this.rangeMin = 0;
    this.rangeMax = this.getInitialRangeMax();

    this.textInputs.forEach((element) => {
      element.addEventListener('change', this.onTextInputChange.bind(this));
      element.addEventListener('input', this.onTextInputInput.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    if (this.minSlider && this.maxSlider) {
      this.setupSliders();

      this.minSlider.addEventListener('input', this.onSliderInput.bind(this));
      this.maxSlider.addEventListener('input', this.onSliderInput.bind(this));

      this.minSlider.addEventListener('change', this.onSliderChange.bind(this));
      this.maxSlider.addEventListener('change', this.onSliderChange.bind(this));
    }

    this.populateDefaultInputValues();
    this.setMinAndMaxValues();
    this.syncSlidersFromTextInputs();
  }

  getInitialRangeMax() {
    if (!this.maxInput) return 0;

    const candidates = [
      this.maxInput.getAttribute('data-max'),
      this.maxInput.getAttribute('placeholder'),
      this.maxInput.value,
    ];

    for (const candidate of candidates) {
      const value = this.parseNumericValue(candidate);
      if (Number.isFinite(value) && value > 0) return value;
    }

    return 0;
  }

  populateDefaultInputValues() {
    if (this.minInput && !this.minInput.value) {
      this.minInput.value = this.formatNumericValue(this.rangeMin);
    }

    if (this.maxInput && !this.maxInput.value && this.rangeMax) {
      this.maxInput.value = this.formatNumericValue(this.rangeMax);
    }
  }

  setupSliders() {
    if (!this.minSlider || !this.maxSlider || !this.rangeMax) return;

    [this.minSlider, this.maxSlider].forEach((slider) => {
      slider.min = String(this.rangeMin);
      slider.max = String(this.rangeMax);
      slider.step = this.getSliderStep();
    });
  }

  getSliderStep() {
    return this.rangeMax > 1000 ? '1' : '0.01';
  }

  onTextInputInput() {
    this.syncSlidersFromTextInputs();
  }

  onTextInputChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncSlidersFromTextInputs();
    this.dispatchFacetInput(event.currentTarget);
  }

  onSliderInput(event) {
    if (!this.minInput || !this.maxInput || !this.minSlider || !this.maxSlider) return;

    let minValue = this.parseNumericValue(this.minSlider.value);
    let maxValue = this.parseNumericValue(this.maxSlider.value);

    if (!Number.isFinite(minValue)) minValue = this.rangeMin;
    if (!Number.isFinite(maxValue)) maxValue = this.rangeMax;

    if (event.currentTarget === this.minSlider && minValue > maxValue) {
      minValue = maxValue;
      this.minSlider.value = String(minValue);
    }

    if (event.currentTarget === this.maxSlider && maxValue < minValue) {
      maxValue = minValue;
      this.maxSlider.value = String(maxValue);
    }

    this.minInput.value = this.formatNumericValue(minValue <= this.rangeMin ? this.rangeMin : minValue);
    this.maxInput.value = this.formatNumericValue(maxValue >= this.rangeMax ? this.rangeMax : maxValue);

    this.setMinAndMaxValues();
    this.updateSliderFill();

    this.dispatchFacetInput(event.currentTarget === this.minSlider ? this.minInput : this.maxInput);
  }

  onSliderChange() {
    this.setMinAndMaxValues();
    this.syncSlidersFromTextInputs();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    if (!this.minInput || !this.maxInput) return;

    if (this.maxInput.value) {
      this.minInput.setAttribute('data-max', this.maxInput.value);
    } else {
      this.minInput.setAttribute('data-max', this.formatNumericValue(this.rangeMax));
    }

    if (this.minInput.value) {
      this.maxInput.setAttribute('data-min', this.minInput.value);
    } else {
      this.maxInput.setAttribute('data-min', String(this.rangeMin));
    }
  }

  adjustToValidValues(input) {
    if (!input) return;

    const value = this.parseNumericValue(input.value);
    const min = this.parseNumericValue(input.getAttribute('data-min'));
    const max = this.parseNumericValue(input.getAttribute('data-max'));

    if (!Number.isFinite(value)) {
      input.value = input === this.maxInput ? this.formatNumericValue(this.rangeMax) : this.formatNumericValue(this.rangeMin);
      return;
    }

    if (Number.isFinite(min) && value < min) {
      input.value = this.formatNumericValue(min);
      return;
    }

    if (Number.isFinite(max) && value > max) {
      input.value = this.formatNumericValue(max);
    }
  }

  syncSlidersFromTextInputs() {
    if (!this.minSlider || !this.maxSlider || !this.minInput || !this.maxInput || !this.rangeMax) return;

    const minValue = this.parseNumericValue(this.minInput.value);
    const maxValue = this.parseNumericValue(this.maxInput.value);

    const safeMin = Number.isFinite(minValue) ? this.clamp(minValue, this.rangeMin, this.rangeMax) : this.rangeMin;
    const safeMax = Number.isFinite(maxValue) ? this.clamp(maxValue, this.rangeMin, this.rangeMax) : this.rangeMax;

    const normalizedMin = Math.min(safeMin, safeMax);
    const normalizedMax = Math.max(safeMin, safeMax);

    this.minSlider.value = String(normalizedMin);
    this.maxSlider.value = String(normalizedMax);

    this.updateSliderFill();
  }

  updateSliderFill() {
    if (!this.rangeFill || !this.minSlider || !this.maxSlider || !this.rangeMax) return;

    const minValue = this.parseNumericValue(this.minSlider.value);
    const maxValue = this.parseNumericValue(this.maxSlider.value);

    const minPercent = ((minValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
    const maxPercent = ((maxValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;

    this.rangeFill.style.left = `${this.clamp(minPercent, 0, 100)}%`;
    this.rangeFill.style.right = `${100 - this.clamp(maxPercent, 0, 100)}%`;
  }

  dispatchFacetInput(input) {
    if (!input) return;

    input.dispatchEvent(
      new Event('input', {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  parseNumericValue(value) {
    if (value === null || value === undefined) return NaN;

    const raw = String(value)
      .trim()
      .replace(/[^\d.,\s'-]/g, '')
      .replace(/\s|'/g, '');

    if (!raw) return NaN;

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const decimalSeparator = lastComma > lastDot ? ',' : '.';

    let normalized = raw;

    if (lastComma !== -1 && lastDot !== -1) {
      if (decimalSeparator === ',') {
        normalized = raw.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = raw.replace(/,/g, '');
      }
    } else if (lastComma !== -1) {
      normalized = raw.replace(',', '.');
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  formatNumericValue(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';

    const rounded = Math.round(number * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
 